import { PresenceType } from '@momentum-ui/react-collaboration';
import { useCallback, useLayoutEffect, useState } from 'react';
import { Logger } from '../../../logger/src/Logger';
import { usePresenceContext } from '../contexts/PresenceContext';

/**
 * UI presence data with converted types
 */
export interface UIPresenceData {
  presenceType: PresenceType;
  presenceLabel: string;
  customStatusMessage?: string;
}

/**
 * Hook return type
 */
export interface UsePresenceReturn {
  /**
   * Get presence data for a user ID
   * Returns undefined if presence is not available
   */
  getPresence: (userId: string) => UIPresenceData | undefined;
  /**
   * Register users for presence subscription
   * Automatically handles deduplication via PresenceManager (SDK worker handles subscription batching)
   * @param options.force — set true after PresenceManager becomes ready so the same id set is pushed through again (workaround for subscribe-before-manager timing)
   */
  registerUsers: (userIds: string[], options?: { force?: boolean }) => void;
}

/**
 * React hook for managing real-time presence subscriptions using centralized PresenceManager
 * 
 * Features:
 * - Uses centralized PresenceManager (single event listener for entire app)
 * - Uses enqueue/dequeue with reference counting (SDK handles deduplication)
 * - Automatically registers/unregisters feature on mount/unmount
 * - Stores presence data in centralized Map (shared across all features)
 * - Listens to real-time updates via PresenceManager's single event listener
 * - Automatically handles cleanup on unmount
 * 
 * @param featureId - Unique identifier for the feature (e.g., 'callHistory', 'speedDial')
 * @returns Object with getPresence and registerUsers methods
 */
export function usePresence(featureId: string): UsePresenceReturn {
  const { manager, presenceMap, version } = usePresenceContext();
  const [userIds, setUserIds] = useState<string[]>([]);

  // useLayoutEffect: register before paint so first frame can show presence after subscription + list fetch
  // Auto-unregister on unmount
  useLayoutEffect(() => {
    if (!manager) {
      Logger.info(`[usePresence] Manager not available for feature ${featureId}`);
      return;
    }

    if (userIds.length > 0) {
      // Register feature with manager
      manager.registerFeature(featureId, userIds).catch((error) => {
        Logger.error(`[usePresence] Failed to register feature ${featureId}: ${error instanceof Error ? error.message : error}`);
      });
    } else {
      // If no users, still register empty set to track feature
      manager.registerFeature(featureId, []).catch((error) => {
        Logger.error(`[usePresence] Failed to register feature ${featureId}: ${error instanceof Error ? error.message : error}`);
      });
    }

    // Cleanup: unregister feature on unmount
    return () => {
      if (manager) {
        manager.unregisterFeature(featureId).catch((error) => {
          Logger.error(`[usePresence] Failed to unregister feature ${featureId}: ${error instanceof Error ? error.message : error}`);
        });
      }
    };
  }, [featureId, userIds, manager]);

  /**
   * Get presence data for a user ID, converted to UI-compatible format
   * Uses PresenceManager's getPresence method
   */
  const getPresence = useCallback((userId: string): UIPresenceData | undefined => {
    if (!manager) {
      return undefined;
    }

    return manager.getPresence(userId);
  }, [manager, presenceMap, version]); // Include version to force re-evaluation when map updates

  /**
   * Register users for presence subscription
   * Updates userIds state only when the set of IDs actually changes (stable comparison)
   * to avoid re-registration loops when callers pass new array references with same contents.
   */
  const registerUsers = useCallback((newUserIds: string[], options?: { force?: boolean }) => {
    setUserIds((prev) => {
      const prevKey = [...prev].sort().join(',');
      const newKey = [...newUserIds].sort().join(',');
      if (!options?.force && prevKey === newKey) {
        return prev;
      }
      return [...newUserIds];
    });
  }, []);

  return {
    getPresence,
    registerUsers,
  };
}
